import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { logUserToMixpanel } from "../utils/mixpanel";
import { useAPI } from "../hooks/useAPI";

const AuthContext = createContext(null);

export const AuthProvider = ({ children, enabled = true }) => {
  const queryClient = useQueryClient();
  const { callAPI } = useAPI();

  // 1. Initialize Token Lazy (Avoids effect flash)
  const [authToken, setAuthToken] = useState(() => {
    return Cookies.get("token") || "";
  });

  // Helper to determine 2FA status
  const is2FA =
    typeof window !== "undefined" &&
    window.location.pathname.toLowerCase().includes("2fa");

  // 2. Define the Query for Fetching User
  const {
    data: loggedInUser = {},
    isLoading,
    error,
    refetch: fetchUser,
  } = useQuery({
    queryKey: ["user", authToken],
    queryFn: async ({ signal }) => {
      const userData = await callAPI(`user`, { token: authToken });

      if (!Object.keys(userData).includes("email")) {
        throw new Error("Invalid user");
      }
      return userData;
    },
    // Only run if we have a token, we aren't in 2FA, and the hook is enabled
    enabled: !!authToken && !is2FA && enabled,
    retry: false, // Don't retry if auth fails (usually means 401)
    staleTime: 1000 * 60 * 5, // Cache user data for 5 minutes
  });

  // 3. Side Effect: Logging to Mixpanel
  useEffect(() => {
    if (loggedInUser && loggedInUser.email) {
      logUserToMixpanel(loggedInUser);
    }
  }, [loggedInUser]);

  // 4. Save Token Helper
  const saveAuthToken = useCallback(
    (token) => {
      const isSecure = window.location.protocol === "https:";

      Cookies.set("token", token, {
        expires: 7,
        secure: isSecure,
        sameSite: isSecure ? "None" : "Lax",
      });

      setAuthToken(token);

      // Invalidate queries so UI updates immediately if switching users
      queryClient.invalidateQueries(["user"]);
    },
    [queryClient]
  );

  // Optional: Add a logout helper
  const logout = useCallback(() => {
    Cookies.remove("token");
    setAuthToken("");
    queryClient.setQueryData(["user", authToken], {});
    queryClient.clear();
  }, [queryClient, authToken]);



  const value = {
    authToken,
    saveAuthToken,
    loggedInUser,
    fetchUser,
    isLoading,
    error,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom Hook to consume the context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
